import React, { Dispatch } from "react";
import { UserState } from "redux-oidc";
import { connect } from "react-redux";
import { RouteComponentProps } from "react-router";
import { showSnack } from "react-redux-snackbar";
import { endpoints, FeatureRequest, BugReport, Feedback } from "../..";
import {
  IAppState,
  LoadingDisplay,
  ErrorDisplay,
  ApiRequest,
  appPaths,
  ForbiddenDisplay
} from "../../../Common";
import {
  getFeedbackDetails,
  resetLoading,
  setLoading,
  updateLoadedFeedback,
  resetLoaded
} from "../../Actions/FeedbackDeleteActions";
import { FeedbackCategory, FeedbackState, FeedbackParent } from "../../Constants/feedbackEnums";
import { FeedbackDetails } from "../FeedbackDetails/FeedbackDetails";
import { FeatureRequestDetails } from "../FeedbackDetails/FeatureRequestDetails";
import { BugReportDetails } from "../FeedbackDetails/BugReportDetails";
import { DeleteFeedback } from "./DeleteFeedback";
import { ResponseResult } from "../../../Common/Models/ResponseResult";
import { ResourceRequest } from "../../Models/Feedback";
import { ResourceRequestDetails } from "../FeedbackDetails/ResourceRequestDetails";

type TParams = {
  code: string;
};

type StateProps = {
  feedback: FeatureRequest | BugReport | ResourceRequest | null;
  contentIsLoaded: boolean;
  isLoading: boolean;
  hasError: boolean;
  errorCode: number | null;
  errorDescription: string;
  user: UserState;
};

type DispatchProps = {
  getFeedbackDetails: (url: string) => void;
  resetLoading: () => void;
  resetLoaded: () => void;
  setLoading: () => void;
  updateLoadedFeedback: (updatedFeedback: FeatureRequest | BugReport) => void;
  showSnack: (uniqueId: any, args: any) => void;
};

type Props = {};

const mapStateToProps = (state: IAppState) => ({
  feedback: state.feedbackDelete.feedback,
  contentIsLoaded: state.feedbackDelete.contentIsLoaded,
  isLoading: state.feedbackDelete.isLoading,
  user: state.oidc
});

const mapDispatchToProps = (dispatch: Dispatch<any>) => ({
  getFeedbackDetails: (code: string) => dispatch(getFeedbackDetails(code)),
  resetLoading: () => dispatch(resetLoading()),
  resetLoaded: () => dispatch(resetLoaded()),
  setLoading: () => dispatch(setLoading()),
  updateLoadedFeedback: (updatedFeedback: FeatureRequest | BugReport) =>
    dispatch(updateLoadedFeedback(updatedFeedback)),
  showSnack: (uniqueId: any, args: any) => dispatch(showSnack(uniqueId, args))
});

class DeleteFeedbackContainer extends React.Component<
  Props & StateProps & DispatchProps & RouteComponentProps<TParams>
> {
  private redirect: boolean;

  private deleteLock: boolean;

  constructor(props: Props & StateProps & DispatchProps & RouteComponentProps<TParams>) {
    super(props);

    this.redirect = false;
    this.deleteLock = false;

    this.onDeleteFeedbackConfirm = this.onDeleteFeedbackConfirm.bind(this);
    this.claimDeleteLock = this.claimDeleteLock.bind(this);
  }

  componentDidMount = () => {
    if (!this.props.isLoading) {
      if (!this.props.contentIsLoaded) {
        this.props.setLoading();
        this.props.getFeedbackDetails(this.props.match.params.code);
      } else {
        const feedbackCode = (this.props.feedback as Feedback).code;
        const paramsCode = this.props.match.params.code;

        if (feedbackCode !== paramsCode) {
          this.props.setLoading();
          this.props.getFeedbackDetails(this.props.match.params.code);
        }
      }
    }
  };

  componentWillUnmount = () => {
    this.props.resetLoading();
  };

  onDeleteFeedbackConfirm(code: string) {
    if (!this.claimDeleteLock()) {
      return;
    }

    ApiRequest.fromPath(this.constructDeleteUrl(code))
      .withAuthHeader()
      .delete()
      .then((response: Response) => {
        response
          .json()
          .then((result: ResponseResult) => {
            if (!response.ok) {
              this.showErrorSnackbar(
                response.status,
                result.error.message,
                "Failed to update feedback"
              );
              this.releaseDeleteLock();
            } else {
              this.props.resetLoaded();
              this.redirect = true;
              this.releaseDeleteLock();
            }
          })
          .catch(() => {
            this.showErrorSnackbar(
              response.status,
              "An unknown error has occured",
              "Failed to update feedback"
            );
            this.releaseDeleteLock();
          })
          .finally(() => {
            if (this.redirect) {
              this.redirectToPost((this.props.feedback as Feedback).code);
            }
          });
      });
  }

  getCategory = (feedback: any) => {
    if (
      (feedback as FeatureRequest).category === FeedbackCategory[FeedbackCategory.FeatureRequest]
    ) {
      return FeedbackCategory.FeatureRequest;
    }
    if ((feedback as BugReport).category === FeedbackCategory[FeedbackCategory.BugReport]) {
      return FeedbackCategory.BugReport;
    }
    if (
      (feedback as ResourceRequest).category === FeedbackCategory[FeedbackCategory.ResourceRequest]
    ) {
      return FeedbackCategory.ResourceRequest;
    }

    throw new Error("Feedback category not found");
  };

  private redirectToPost = (code: string) => {
    if (this.props.feedback) {
      let path: string = "";
      const category: FeedbackCategory = this.getCategory(this.props.feedback);

      switch (category) {
        case FeedbackCategory.FeatureRequest: {
          switch (this.props.feedback.parent) {
            case FeedbackParent.Application:
              path = appPaths.app.featureRequests.replace(
                ":id",
                this.props.feedback.application.rolePrefix
              );
              break;
            case FeedbackParent.Topic:
              path = appPaths.topic.featureRequests.replace(
                ":topicCode",
                this.props.feedback.topic.code
              );
              break;
            default:
              throw new Error("Invalid feedback parent");
          }
          break;
        }
        case FeedbackCategory.BugReport: {
          switch (this.props.feedback.parent) {
            case FeedbackParent.Application:
              path = appPaths.app.bugReports.replace(
                ":id",
                this.props.feedback.application.rolePrefix
              );
              break;
            case FeedbackParent.Topic:
              path = appPaths.topic.bugReports.replace(
                ":topicCode",
                this.props.feedback.topic.code
              );
              break;
            default:
              throw new Error("Invalid feedback parent");
          }
          break;
        }
        default:
          throw new Error("Invalid feedback category");
      }

      this.props.history.push(path);
    }
  };

  showErrorSnackbar = (errorCode: number, errorDescription: string, reason: string) => {
    this.props.showSnack("EDIT_FEEDBACK", {
      label: `${reason}: ${errorDescription} (Error Code ${errorCode})`,
      timeout: 5000
    });
  };

  constructDeleteUrl(feedbackCode: string) {
    return endpoints.feedback.delete(feedbackCode);
  }

  claimDeleteLock() {
    if (!this.deleteLock) {
      this.deleteLock = true;
      return true;
    }
    return false;
  }

  releaseDeleteLock() {
    this.deleteLock = false;
  }

  render() {
    if (this.props.hasError) {
      return (
        <ErrorDisplay
          errorCode={this.props.errorCode as number}
          errorDescription={this.props.errorDescription}
          errorResult="Failed to load feedback"
        />
      );
    }

    if (this.props.contentIsLoaded && !this.props.isLoading) {
      const category: FeedbackCategory = this.getCategory(this.props.feedback);

      if (!this.props.feedback || !this.props.feedback.permissions) {
        throw Error("Permissions must be loaded to display this page");
      }

      if (!this.props.feedback.permissions.canDelete) {
        // we do not have edit permission
        return (
          <ForbiddenDisplay description="You do not have permission to delete this feedback" />
        );
      }

      switch (category) {
        case FeedbackCategory.FeatureRequest: {
          const featureRequest: FeatureRequest = this.props.feedback as FeatureRequest;

          return (
            <DeleteFeedback
              title={featureRequest.title}
              authorData={featureRequest.authorData}
              dateCreated={new Date(Date.parse(featureRequest.dateCreated))}
              dateModified={
                featureRequest.dateModified === null
                  ? null
                  : new Date(Date.parse(featureRequest.dateModified))
              }
              state={FeedbackState[featureRequest.state as keyof typeof FeedbackState]}
              category={category}
              code={this.props.match.params.code}
              onDeleteConfirm={this.onDeleteFeedbackConfirm}
            >
              <FeatureRequestDetails feedback={featureRequest} user={this.props.user} />
            </DeleteFeedback>
          );
        }
        case FeedbackCategory.BugReport: {
          const bugReport: BugReport = this.props.feedback as BugReport;

          return (
            <DeleteFeedback
              title={bugReport.title}
              authorData={bugReport.authorData}
              dateCreated={new Date(Date.parse(bugReport.dateCreated))}
              dateModified={
                bugReport.dateModified === null
                  ? null
                  : new Date(Date.parse(bugReport.dateModified))
              }
              state={FeedbackState[bugReport.state as keyof typeof FeedbackState]}
              category={category}
              code={this.props.match.params.code}
              onDeleteConfirm={this.onDeleteFeedbackConfirm}
            >
              <BugReportDetails feedback={bugReport} user={this.props.user} />
            </DeleteFeedback>
          );
        }
        case FeedbackCategory.ResourceRequest: {
          const resourceRequest: ResourceRequest = this.props.feedback as ResourceRequest;

          return (
            <DeleteFeedback
              title={resourceRequest.title}
              authorData={resourceRequest.authorData}
              dateCreated={new Date(Date.parse(resourceRequest.dateCreated))}
              dateModified={
                resourceRequest.dateModified === null
                  ? null
                  : new Date(Date.parse(resourceRequest.dateModified))
              }
              state={FeedbackState[resourceRequest.state as keyof typeof FeedbackState]}
              category={category}
              code={this.props.match.params.code}
              onDeleteConfirm={this.onDeleteFeedbackConfirm}
            >
              <ResourceRequestDetails feedback={resourceRequest} user={this.props.user} />
            </DeleteFeedback>
          );
        }
        default:
          throw new Error("Invalid feedback category");
      }
    }

    return <LoadingDisplay />;
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(DeleteFeedbackContainer);
